import { z } from "zod";
import { connectedDecisionCitationId, connectedFactCitationId, } from "./subject-connected-knowledge.js";
/**
 * Phase 4B — deterministic Subject intelligence projection.
 *
 * Derived from the 4A bundle plus auditable events. No LLM.
 * Window is calendar time from `generatedAt`, not “since last visit”.
 */
export const SUBJECT_INTELLIGENCE_WINDOW_DAYS = 30;
export const SUBJECT_CHANGE_PREVIEW_LIMIT = 8;
export const SUBJECT_CHANGE_MUTATION_OVERLAY_MS = 5_000;
export const SUBJECT_ATTENTION_CATEGORIES = [
    "superseded",
    "open_question",
    "competing_facts",
    "evidence_pending",
    "data_quality",
];
export const SubjectAttentionCategorySchema = z.enum(SUBJECT_ATTENTION_CATEGORIES);
export const SUBJECT_CHANGE_TYPES = [
    "node_added",
    "node_edited",
    "relationship_added",
    "relationship_edited",
    "relationship_removed",
    "association_linked",
    "association_unlinked",
    "fact_recorded",
    "decision_recorded",
    "question_captured",
    "knowledge_included",
    "knowledge_removed",
];
export const SubjectChangeTypeSchema = z.enum(SUBJECT_CHANGE_TYPES);
export const SubjectIntelligenceWindowSchema = z.object({
    from: z.string().datetime(),
    to: z.string().datetime(),
    days: z.number().int().positive(),
    label: z.string().min(1),
});
export const SubjectChangeItemSchema = z.object({
    id: z.string().min(1),
    type: SubjectChangeTypeSchema,
    at: z.string().datetime(),
    summary: z.string().min(1),
    citationId: z.string().min(1).nullable(),
    sourceKind: z.enum(["mutation", "audit", "fact", "decision", "question", "association"]),
    sourceId: z.string().min(1),
    /** True when `summary` is a safe human description (Level 1). */
    material: z.boolean().default(false),
    /** Underlying mutation/op wording for Audit. */
    auditSummary: z.string().min(1).optional(),
});
export const SubjectAttentionItemSchema = z.object({
    id: z.string().min(1),
    category: SubjectAttentionCategorySchema,
    summary: z.string().min(1),
    citationIds: z.array(z.string().min(1)),
});
export const SubjectIntelligenceSchema = z.object({
    window: SubjectIntelligenceWindowSchema,
    previewLimit: z.number().int().positive(),
    changes: z.array(SubjectChangeItemSchema),
    attention: z.array(SubjectAttentionItemSchema),
});
const ALLOWED_MUTATION_OPS = new Set([
    "node_add",
    "node_edit",
    "node_del",
    "edge_add",
    "edge_edit",
    "edge_del",
    "edge_archive",
]);
export function subjectIntelligenceWindow(generatedAt, days = SUBJECT_INTELLIGENCE_WINDOW_DAYS) {
    const to = new Date(generatedAt);
    const from = new Date(to.getTime() - days * 24 * 60 * 60 * 1000);
    return {
        from: from.toISOString(),
        to: to.toISOString(),
        days,
        label: `last ${days} days`,
    };
}
function inWindow(iso, window) {
    if (!iso)
        return false;
    const t = Date.parse(iso);
    if (Number.isNaN(t))
        return false;
    return t >= Date.parse(window.from) && t <= Date.parse(window.to);
}
function asOfDay(asOf) {
    if (!asOf)
        return null;
    const match = asOf.match(/^(\d{4}-\d{2}-\d{2})/);
    return match?.[1] ?? null;
}
function factIdentityValue(fact) {
    return `${fact.value}\0${fact.unit ?? ""}`;
}
/**
 * Same key + different values: competing when as-of days match or either
 * as-of is missing/unparseable. Distinct parseable as-of days are history.
 */
export function factsAreCompeting(a, b) {
    if (factIdentityValue(a) === factIdentityValue(b))
        return false;
    const dayA = asOfDay(a.asOf);
    const dayB = asOfDay(b.asOf);
    if (dayA && dayB && dayA !== dayB)
        return false;
    return true;
}
export function isMalformedLegacyFact(fact) {
    return !fact.source?.trim() || !fact.asOf?.trim();
}
function payloadEndpoint(payload, key) {
    if (!payload)
        return null;
    const value = payload[key];
    return typeof value === "string" ? value : null;
}
function payloadRel(payload) {
    if (!payload)
        return null;
    const value = payload.rel;
    return typeof value === "string" ? value : null;
}
function mutationChangeType(op) {
    switch (op) {
        case "node_add":
            return "node_added";
        case "node_edit":
        case "node_del":
            return "node_edited";
        case "edge_add":
            return "relationship_added";
        case "edge_edit":
            return "relationship_edited";
        case "edge_del":
        case "edge_archive":
            return "relationship_removed";
        default:
            return null;
    }
}
function clipChange(text, max) {
    const trimmed = text.trim();
    if (trimmed.length <= max)
        return trimmed;
    return `${trimmed.slice(0, Math.max(0, max - 1))}…`;
}
function factRecordedSummary(fact) {
    const key = fact.key.replaceAll("_", " ");
    const value = `${fact.value}${fact.unit ? ` ${fact.unit}` : ""}`;
    return `${key} recorded as ${clipChange(value, 120)}`;
}
function payloadFacts(payload) {
    if (!payload)
        return [];
    const context = payload.context;
    if (!context || typeof context !== "object")
        return [];
    const facts = context.facts;
    if (!Array.isArray(facts))
        return [];
    const out = [];
    for (const row of facts) {
        if (!row || typeof row !== "object")
            continue;
        const rec = row;
        if (typeof rec.key !== "string" || typeof rec.value !== "string")
            continue;
        out.push({
            key: rec.key,
            value: rec.value,
            unit: typeof rec.unit === "string" ? rec.unit : null,
            asOf: typeof rec.asOf === "string" ? rec.asOf : "",
            recordedAt: typeof rec.recordedAt === "string" ? rec.recordedAt : "",
            citationId: "",
        });
    }
    return out;
}
function payloadDecisions(payload) {
    if (!payload)
        return [];
    const context = payload.context;
    if (!context || typeof context !== "object")
        return [];
    const decisions = context.decisions;
    if (!Array.isArray(decisions))
        return [];
    const out = [];
    for (const row of decisions) {
        if (!row || typeof row !== "object")
            continue;
        const text = row.text;
        if (typeof text === "string" && text.trim())
            out.push({ text: text.trim() });
    }
    return out;
}
function factIdentity(fact) {
    return `${fact.key}\0${fact.value}\0${fact.unit ?? ""}\0${fact.asOf}`;
}
function materialMutationSummary(event, ctx) {
    if (event.op === "node_add")
        return "Subject created";
    if (event.op === "node_del")
        return "Subject archived or removed";
    if (event.op === "node_edit") {
        const beforeFacts = new Set(payloadFacts(event.before).map(factIdentity));
        const addedFacts = payloadFacts(event.after).filter((fact) => !beforeFacts.has(factIdentity(fact)));
        if (addedFacts.length === 1 && addedFacts[0]) {
            return factRecordedSummary(addedFacts[0]);
        }
        if (addedFacts.length > 1 && addedFacts.length <= 3) {
            return `Facts updated (${addedFacts.map((fact) => fact.key.replaceAll("_", " ")).join(", ")})`;
        }
        const beforeDecisions = new Set(payloadDecisions(event.before).map((row) => row.text));
        const addedDecisions = payloadDecisions(event.after).filter((row) => !beforeDecisions.has(row.text));
        if (addedDecisions.length === 1 && addedDecisions[0]) {
            return `Decision recorded: ${clipChange(addedDecisions[0].text, 160)}`;
        }
        return null;
    }
    if (event.op === "edge_add" ||
        event.op === "edge_edit" ||
        event.op === "edge_del" ||
        event.op === "edge_archive") {
        const rel = payloadRel(event.after) ?? payloadRel(event.before);
        const from = payloadEndpoint(event.after, "from") ?? payloadEndpoint(event.before, "from");
        const to = payloadEndpoint(event.after, "to") ?? payloadEndpoint(event.before, "to");
        const otherId = from === ctx.subjectId ? to : to === ctx.subjectId ? from : (from ?? to);
        const other = otherId ? ctx.related.find((row) => row.nodeId === otherId) : undefined;
        if (!rel || !other)
            return null;
        if (event.op === "edge_add")
            return `Linked as ${rel} → ${other.label}`;
        if (event.op === "edge_edit")
            return `Updated ${rel} link to ${other.label}`;
        return `Removed ${rel} link to ${other.label}`;
    }
    return null;
}
export function selectLevel1MaterialChanges(changes, limit = SUBJECT_LEVEL1_CHANGE_LIMIT) {
    return changes.filter((item) => item.material).slice(0, limit);
}
function mutationSummary(event) {
    const rel = payloadRel(event.after) ?? payloadRel(event.before);
    switch (event.op) {
        case "node_add":
            return "Subject created";
        case "node_edit":
            return "Subject record updated";
        case "node_del":
            return "Subject archived or removed";
        case "edge_add":
            return rel ? `Relationship added (${rel})` : "Relationship added";
        case "edge_edit":
            return rel ? `Relationship updated (${rel})` : "Relationship updated";
        case "edge_del":
            return rel ? `Relationship removed (${rel})` : "Relationship removed";
        case "edge_archive":
            return rel ? `Relationship archived (${rel})` : "Relationship archived";
        default:
            return `Graph change (${event.op})`;
    }
}
function mutationCovers(mutations, at, predicate) {
    const t = Date.parse(at);
    if (Number.isNaN(t))
        return false;
    return mutations.some((event) => {
        if (!predicate(event))
            return false;
        const delta = Math.abs(Date.parse(event.createdAt) - t);
        return delta <= SUBJECT_CHANGE_MUTATION_OVERLAY_MS;
    });
}
/**
 * Dedup: one UI event per underlying action.
 *
 * 1. Mutation ledger rows are canonical for graph writes (`mutation:{id}`).
 * 2. Association audit rows are canonical for link/unlink (`audit:{id}`).
 * 3. Fact/decision/question overlay events are emitted only when no mutation
 *    of the covering type exists within 5s of the overlay timestamp:
 *    - fact_recorded / decision_recorded ← subject `node_add` or `node_edit`
 *    - question_captured ← incident `edge_add` with rel `questions`
 * 4. Association `createdAt` is not used when an audit create exists for
 *    the same association id.
 */
export const SUBJECT_LEVEL1_CHANGE_LIMIT = 5;
export function deriveSubjectChanges(input) {
    const items = [];
    const windowedMutations = input.mutations.filter((event) => ALLOWED_MUTATION_OPS.has(event.op) &&
        inWindow(event.createdAt, input.window) &&
        Date.parse(event.createdAt) <= Date.parse(input.window.to));
    const related = input.related ?? [];
    for (const event of windowedMutations) {
        const type = mutationChangeType(event.op);
        if (!type)
            continue;
        const auditSummary = mutationSummary(event);
        const materialSummary = materialMutationSummary(event, {
            subjectId: input.subjectId,
            related,
        });
        items.push({
            id: `mutation:${event.id}`,
            type,
            at: event.createdAt,
            summary: materialSummary ?? auditSummary,
            citationId: null,
            sourceKind: "mutation",
            sourceId: event.id,
            material: Boolean(materialSummary),
            auditSummary,
        });
    }
    const auditedCreate = new Set();
    for (const event of input.associationEvents) {
        if (!inWindow(event.at, input.window))
            continue;
        const linked = event.action === "create";
        if (linked)
            auditedCreate.add(event.associationId);
        const title = input.associations.find((row) => row.objectId === event.objectId)?.title;
        const objectLabel = event.objectType.replaceAll("_", " ");
        const summary = linked
            ? title
                ? `Linked ${objectLabel} ${title}`
                : `Linked ${objectLabel}`
            : title
                ? `Unlinked ${objectLabel} ${title}`
                : `Unlinked ${objectLabel}`;
        items.push({
            id: `audit:${event.id}`,
            type: linked ? "association_linked" : "association_unlinked",
            at: event.at,
            summary,
            citationId: `assoc:${event.objectType}:${event.objectId}`,
            sourceKind: "audit",
            sourceId: event.id,
            material: true,
            auditSummary: linked ? `Linked ${objectLabel}` : `Unlinked ${objectLabel}`,
        });
    }
    for (const association of input.associations) {
        if (auditedCreate.has(association.id))
            continue;
        if (!inWindow(association.createdAt, input.window))
            continue;
        const objectLabel = association.objectType.replaceAll("_", " ");
        const summary = association.title
            ? `Linked ${objectLabel} ${association.title}`
            : `Linked ${objectLabel}`;
        items.push({
            id: `association:${association.id}:create`,
            type: "association_linked",
            at: association.createdAt,
            summary,
            citationId: association.citationId,
            sourceKind: "association",
            sourceId: association.id,
            material: true,
            auditSummary: `Linked ${objectLabel}`,
        });
    }
    for (const fact of input.facts) {
        if (!inWindow(fact.recordedAt, input.window))
            continue;
        if (mutationCovers(windowedMutations, fact.recordedAt, (event) => Boolean((event.op === "node_edit" || event.op === "node_add") &&
            event.targetKind === "node" &&
            event.targetId === input.subjectId))) {
            continue;
        }
        items.push({
            id: `overlay:${fact.citationId}`,
            type: "fact_recorded",
            at: fact.recordedAt,
            summary: factRecordedSummary(fact),
            citationId: fact.citationId,
            sourceKind: "fact",
            sourceId: fact.citationId,
            material: true,
            auditSummary: `Fact recorded (${fact.key})`,
        });
    }
    for (const decision of input.decisions) {
        if (!inWindow(decision.at, input.window))
            continue;
        if (mutationCovers(windowedMutations, decision.at, (event) => Boolean((event.op === "node_edit" || event.op === "node_add") &&
            event.targetKind === "node" &&
            event.targetId === input.subjectId))) {
            continue;
        }
        items.push({
            id: `overlay:${decision.citationId}`,
            type: "decision_recorded",
            at: decision.at,
            summary: decision.text?.trim()
                ? `Decision recorded: ${clipChange(decision.text, 160)}`
                : "Decision recorded",
            citationId: decision.citationId,
            sourceKind: "decision",
            sourceId: decision.citationId,
            material: true,
            auditSummary: "Decision recorded",
        });
    }
    for (const question of input.openQuestions) {
        const createdAt = input.questionCreatedAt[question.nodeId];
        if (!createdAt || !inWindow(createdAt, input.window))
            continue;
        if (mutationCovers(windowedMutations, createdAt, (event) => {
            if (event.op !== "edge_add")
                return false;
            const from = payloadEndpoint(event.after, "from");
            const to = payloadEndpoint(event.after, "to");
            const rel = payloadRel(event.after);
            return from === question.nodeId && to === input.subjectId && rel === "questions";
        })) {
            continue;
        }
        items.push({
            id: `overlay:${question.citationId}`,
            type: "question_captured",
            at: createdAt,
            summary: `Open question added: ${clipChange(question.label, 160)}`,
            citationId: question.citationId,
            sourceKind: "question",
            sourceId: question.nodeId,
            material: true,
            auditSummary: `Open question captured (${question.label})`,
        });
    }
    const originLabel = (originSubjectId, fallback) => input.connected?.origins.find((row) => row.originSubjectId === originSubjectId)?.label ??
        fallback;
    for (const fact of input.connected?.facts ?? []) {
        if (!inWindow(fact.recordedAt, input.window))
            continue;
        const citationId = connectedFactCitationId(fact.originSubjectId, fact.citationId);
        const overlayAt = fact.recordedAt;
        const label = originLabel(fact.originSubjectId, "Included origin");
        const key = fact.key.replaceAll("_", " ");
        const value = `${fact.value}${fact.unit ? ` ${fact.unit}` : ""}`;
        items.push({
            id: `overlay:${citationId}`,
            type: "fact_recorded",
            at: overlayAt,
            summary: `${label} · ${key} recorded as ${clipChange(value, 120)}`,
            citationId,
            sourceKind: "fact",
            sourceId: citationId,
            material: true,
            auditSummary: `Connected fact recorded (${fact.key})`,
        });
    }
    for (const origin of input.connected?.origins ?? []) {
        for (const decision of origin.decisions) {
            if (!inWindow(decision.at, input.window))
                continue;
            const citationId = connectedDecisionCitationId(origin.originSubjectId, decision.citationId);
            items.push({
                id: `overlay:${citationId}`,
                type: "decision_recorded",
                at: decision.at,
                summary: `${origin.label} · decision recorded: ${clipChange(decision.text, 160)}`,
                citationId,
                sourceKind: "decision",
                sourceId: citationId,
                material: true,
                auditSummary: "Connected decision recorded",
            });
        }
    }
    for (const event of input.scopeEvents ?? []) {
        if (!inWindow(event.at, input.window))
            continue;
        const included = event.action === "create";
        items.push({
            id: `audit:${event.id}`,
            type: included ? "knowledge_included" : "knowledge_removed",
            at: event.at,
            summary: included
                ? `${event.originLabel} knowledge added to this Subject`
                : `${event.originLabel} knowledge removed from this Subject`,
            citationId: null,
            sourceKind: "audit",
            sourceId: event.id,
            material: true,
            auditSummary: included
                ? "Knowledge-scope inclusion created"
                : "Knowledge-scope inclusion archived",
        });
    }
    items.sort((a, b) => {
        if (a.at !== b.at)
            return a.at < b.at ? 1 : -1;
        if (a.type !== b.type)
            return a.type.localeCompare(b.type);
        return a.id.localeCompare(b.id);
    });
    return items;
}
/**
 * Open question is attention-worthy iff it is an active depth-1 neighbour
 * `open-question --questions--> Subject` and no `addresses` edge to that
 * same question node is present in the permission-filtered related set.
 *
 * `addresses` is the existing inverse narrative (Subject answering/closing
 * a question). Prose, facts, decisions, and other neighbours are not
 * treated as resolution.
 */
export function isOpenQuestionResolved(questionNodeId, related) {
    return related.some((row) => row.nodeId === questionNodeId && row.relation === "addresses");
}
export function deriveSubjectAttention(input) {
    const items = [];
    if (input.superseded) {
        items.push({
            id: "attention:superseded",
            category: "superseded",
            summary: input.superseded.byLabel
                ? `This subject was superseded: ${input.superseded.reason}`
                : `This subject was superseded: ${input.superseded.reason}`,
            citationIds: [input.identityCitationId],
        });
    }
    const questioned = input.related.filter((row) => row.kind === "open-question" && row.relation === "questions");
    for (const question of questioned) {
        if (isOpenQuestionResolved(question.nodeId, input.related))
            continue;
        items.push({
            id: `attention:question:${question.nodeId}`,
            category: "open_question",
            summary: question.label,
            citationIds: [question.citationId],
        });
    }
    const byKey = new Map();
    for (const fact of input.facts) {
        if (fact.superseded)
            continue;
        const list = byKey.get(fact.key) ?? [];
        list.push(fact);
        byKey.set(fact.key, list);
    }
    for (const [key, group] of [...byKey.entries()].sort(([a], [b]) => a.localeCompare(b))) {
        const competing = new Set();
        for (let i = 0; i < group.length; i += 1) {
            for (let j = i + 1; j < group.length; j += 1) {
                const left = group[i];
                const right = group[j];
                if (!left || !right)
                    continue;
                if (factsAreCompeting(left, right)) {
                    competing.add(left.citationId);
                    competing.add(right.citationId);
                }
            }
        }
        if (competing.size > 0) {
            items.push({
                id: `attention:competing:${key}`,
                category: "competing_facts",
                summary: `Competing recorded values for ${key.replaceAll("_", " ")}`,
                citationIds: [...competing].sort(),
            });
        }
    }
    for (const evidence of input.evidence) {
        if (evidence.unavailableReason === "restricted")
            continue;
        if (evidence.unavailableReason === "truncated")
            continue;
        const pending = evidence.status === "identified" ||
            (evidence.status === "unavailable" && Boolean(evidence.unavailableReason));
        if (!pending)
            continue;
        const reason = evidence.unavailableReason ?? "not retrieved";
        items.push({
            id: `attention:evidence:${evidence.citationId}`,
            category: "evidence_pending",
            summary: evidence.title
                ? `Evidence pending (${evidence.title})`
                : `Attached evidence is not yet retrievable (${reason})`,
            citationIds: [evidence.citationId],
        });
    }
    const seenLegacy = new Set();
    for (const fact of input.legacyFacts) {
        if (!isMalformedLegacyFact(fact))
            continue;
        const id = `attention:data-quality:${fact.key}`;
        if (seenLegacy.has(id))
            continue;
        seenLegacy.add(id);
        items.push({
            id,
            category: "data_quality",
            summary: `Fact "${fact.key}" is missing required source or as-of`,
            citationIds: fact.asOf?.trim() ? [`fact:${fact.key}:${fact.asOf}`] : [],
        });
    }
    const rank = new Map(SUBJECT_ATTENTION_CATEGORIES.map((category, index) => [category, index]));
    items.sort((a, b) => {
        const ra = rank.get(a.category) ?? 99;
        const rb = rank.get(b.category) ?? 99;
        if (ra !== rb)
            return ra - rb;
        return a.id.localeCompare(b.id);
    });
    return items;
}
export function deriveSubjectIntelligence(input) {
    const window = subjectIntelligenceWindow(input.generatedAt);
    const sources = input.sources ?? {
        mutations: [],
        associationEvents: [],
        questionCreatedAt: {},
        legacyFacts: [],
    };
    const changes = deriveSubjectChanges({
        subjectId: input.subjectId,
        window,
        mutations: sources.mutations,
        associationEvents: sources.associationEvents,
        associations: input.canonical.associations,
        facts: input.canonical.facts,
        decisions: input.canonical.decisions,
        openQuestions: input.canonical.openQuestions,
        questionCreatedAt: sources.questionCreatedAt,
        related: input.canonical.related,
        connected: input.connected,
        scopeEvents: sources.scopeEvents,
    });
    const attention = deriveSubjectAttention({
        subjectId: input.subjectId,
        identityCitationId: input.canonical.identity.citationId,
        superseded: input.canonical.superseded,
        related: input.canonical.related,
        facts: input.canonical.facts,
        evidence: input.canonical.evidence,
        legacyFacts: sources.legacyFacts,
    });
    return SubjectIntelligenceSchema.parse({
        window,
        previewLimit: SUBJECT_CHANGE_PREVIEW_LIMIT,
        changes,
        attention,
    });
}
//# sourceMappingURL=subject-intelligence.js.map